import React, { useState } from 'react';
import { Shield, FileText, Calendar, User, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const Garantias: React.FC = () => {
  const [activeTab, setActiveTab] = useState('inscripcion');

  const tabs = [
    { id: 'inscripcion', label: 'Inscripción de Prenda', icon: Shield },
    { id: 'levantamiento', label: 'Levantamiento de Prenda', icon: ArrowRight }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Garantías</h1>
        <p className="text-gray-600">Gestiona inscripciones y levantamientos de prendas vehiculares</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 inline mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'inscripcion' && <InscripcionPrenda />}
      {activeTab === 'levantamiento' && <LevantamientoPrenda />}
    </div>
  );
};

const InscripcionPrenda: React.FC = () => {
  const [formData, setFormData] = useState({
    // Datos del vehículo
    placa: '',
    numeroTarjeta: '',
    
    // Datos del propietario
    propietario: {
      tipoDocumento: '',
      numeroDocumento: '',
      nombre: '',
      apellido: '',
      telefono: '',
      email: ''
    },
    
    // Datos del acreedor
    acreedor: {
      tipoDocumento: '',
      numeroDocumento: '',
      nombre: '',
      apellido: '',
      telefono: '',
      email: '',
      direccion: ''
    },
    
    // Datos de la prenda
    fechaInscripcion: '',
    valorPrenda: '',
    fechaVencimiento: '',
    observaciones: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('propietario.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        propietario: {
          ...prev.propietario,
          [field]: value
        }
      }));
    } else if (name.startsWith('acreedor.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        acreedor: {
          ...prev.acreedor,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.placa) {
      toast.error('Por favor ingresa la placa del vehículo');
      return;
    }
    
    if (!formData.acreedor.tipoDocumento || !formData.acreedor.numeroDocumento) {
      toast.error('Por favor completa los datos del acreedor');
      return;
    }
    
    toast.success('Inscripción de prenda procesada exitosamente');
    console.log('Datos de la prenda:', formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Datos del Vehículo */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <FileText className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900">Datos del Vehículo</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">Placa del Vehículo *</label>
            <input
              type="text"
              name="placa"
              value={formData.placa}
              onChange={handleInputChange}
              className="input"
              placeholder="Ej: ABC123"
              required
            />
          </div>
          
          <div>
            <label className="label">Número de Tarjeta de Propiedad</label>
            <input
              type="text"
              name="numeroTarjeta"
              value={formData.numeroTarjeta}
              onChange={handleInputChange}
              className="input"
              placeholder="Número de tarjeta"
            />
          </div>
        </div>
      </div>

      {/* Propietario */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <User className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900">Propietario del Vehículo</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="label">Tipo de Documento</label>
            <select
              name="propietario.tipoDocumento"
              value={formData.propietario.tipoDocumento}
              onChange={handleInputChange}
              className="input"
            >
              <option value="">Seleccione tipo</option>
              <option value="CC">Cédula de Ciudadanía</option>
              <option value="CE">Cédula de Extranjería</option>
              <option value="NIT">NIT</option>
            </select>
          </div>
          
          <div>
            <label className="label">Número de Documento</label>
            <input
              type="text"
              name="propietario.numeroDocumento"
              value={formData.propietario.numeroDocumento}
              onChange={handleInputChange}
              className="input"
              placeholder="Número de documento"
            />
          </div>
          
          <div>
            <label className="label">Nombre</label>
            <input
              type="text"
              name="propietario.nombre"
              value={formData.propietario.nombre}
              onChange={handleInputChange}
              className="input"
              placeholder="Nombre"
            />
          </div>
          
          <div>
            <label className="label">Apellido</label>
            <input
              type="text"
              name="propietario.apellido"
              value={formData.propietario.apellido}
              onChange={handleInputChange}
              className="input"
              placeholder="Apellido"
            />
          </div>
          
          <div>
            <label className="label">Teléfono</label>
            <input
              type="tel"
              name="propietario.telefono"
              value={formData.propietario.telefono}
              onChange={handleInputChange}
              className="input"
              placeholder="Teléfono"
            />
          </div>
          
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              name="propietario.email"
              value={formData.propietario.email}
              onChange={handleInputChange}
              className="input"
              placeholder="Email"
            />
          </div>
        </div>
      </div>

      {/* Acreedor */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Shield className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900">Acreedor (Entidad Financiera)</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="label">Tipo de Documento *</label>
            <select
              name="acreedor.tipoDocumento"
              value={formData.acreedor.tipoDocumento}
              onChange={handleInputChange}
              className="input"
              required
            >
              <option value="">Seleccione tipo</option>
              <option value="CC">Cédula de Ciudadanía</option>
              <option value="CE">Cédula de Extranjería</option>
              <option value="NIT">NIT</option>
            </select>
          </div>
          
          <div>
            <label className="label">Número de Documento *</label>
            <input
              type="text"
              name="acreedor.numeroDocumento"
              value={formData.acreedor.numeroDocumento}
              onChange={handleInputChange}
              className="input"
              placeholder="Número de documento"
              required
            />
          </div>
          
          <div>
            <label className="label">Nombre/Razón Social *</label>
            <input
              type="text"
              name="acreedor.nombre"
              value={formData.acreedor.nombre}
              onChange={handleInputChange}
              className="input"
              placeholder="Nombre o razón social"
              required
            />
          </div>
          
          <div>
            <label className="label">Apellido</label>
            <input
              type="text"
              name="acreedor.apellido"
              value={formData.acreedor.apellido}
              onChange={handleInputChange}
              className="input"
              placeholder="Apellido"
            />
          </div>
          
          <div>
            <label className="label">Teléfono</label>
            <input
              type="tel"
              name="acreedor.telefono"
              value={formData.acreedor.telefono}
              onChange={handleInputChange}
              className="input"
              placeholder="Teléfono"
            />
          </div>
          
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              name="acreedor.email"
              value={formData.acreedor.email}
              onChange={handleInputChange}
              className="input"
              placeholder="Email"
            />
          </div>
          
          <div className="md:col-span-2 lg:col-span-3">
            <label className="label">Dirección</label>
            <input
              type="text"
              name="acreedor.direccion"
              value={formData.acreedor.direccion}
              onChange={handleInputChange}
              className="input"
              placeholder="Dirección completa"
            />
          </div>
        </div>
      </div>

      {/* Datos de la Prenda */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Calendar className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900">Información de la Prenda</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">Fecha de Inscripción</label>
            <input
              type="date"
              name="fechaInscripcion"
              value={formData.fechaInscripcion}
              onChange={handleInputChange}
              className="input"
            />
          </div>
          
          <div>
            <label className="label">Valor de la Prenda</label>
            <input
              type="number"
              name="valorPrenda"
              value={formData.valorPrenda}
              onChange={handleInputChange}
              className="input"
              placeholder="Valor en pesos"
              min="0"
            />
          </div>
          
          <div>
            <label className="label">Fecha de Vencimiento</label>
            <input
              type="date"
              name="fechaVencimiento"
              value={formData.fechaVencimiento}
              onChange={handleInputChange}
              className="input"
            />
          </div>
          
          <div>
            <label className="label">Observaciones</label>
            <textarea
              name="observaciones"
              value={formData.observaciones}
              onChange={handleInputChange}
              className="input"
              rows={3}
              placeholder="Observaciones adicionales"
            />
          </div>
        </div>
      </div>

      {/* Botones de Acción */}
      <div className="flex justify-end space-x-4">
        <button type="button" className="btn btn-secondary">
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary">
          <Shield className="w-4 h-4 mr-2" />
          Inscribir Prenda
        </button>
      </div>
    </form>
  );
};

const LevantamientoPrenda: React.FC = () => {
  const [formData, setFormData] = useState({
    placa: '',
    numeroPrenda: '',
    fechaLevantamiento: '',
    motivoLevantamiento: '',
    observaciones: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.placa) {
      toast.error('Por favor ingresa la placa del vehículo');
      return;
    }
    
    toast.success('Levantamiento de prenda procesado exitosamente');
    console.log('Datos del levantamiento:', formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <ArrowRight className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900">Levantamiento de Prenda</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">Placa del Vehículo *</label>
            <input
              type="text"
              name="placa"
              value={formData.placa}
              onChange={handleInputChange}
              className="input"
              placeholder="Ej: ABC123"
              required
            />
          </div>
          
          <div>
            <label className="label">Número de Prenda</label>
            <input
              type="text"
              name="numeroPrenda"
              value={formData.numeroPrenda}
              onChange={handleInputChange}
              className="input"
              placeholder="Número de prenda"
            />
          </div>
          
          <div>
            <label className="label">Fecha de Levantamiento</label>
            <input
              type="date"
              name="fechaLevantamiento"
              value={formData.fechaLevantamiento}
              onChange={handleInputChange}
              className="input"
            />
          </div>
          
          <div>
            <label className="label">Motivo del Levantamiento</label>
            <select
              name="motivoLevantamiento"
              value={formData.motivoLevantamiento}
              onChange={handleInputChange}
              className="input"
            >
              <option value="">Seleccione motivo</option>
              <option value="pago">Pago de la deuda</option>
              <option value="refinanciacion">Refinanciación</option>
              <option value="venta">Venta del vehículo</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="label">Observaciones</label>
            <textarea
              name="observaciones"
              value={formData.observaciones}
              onChange={handleInputChange}
              className="input"
              rows={3}
              placeholder="Observaciones adicionales"
            />
          </div>
        </div>
      </div>

      {/* Botones de Acción */}
      <div className="flex justify-end space-x-4">
        <button type="button" className="btn btn-secondary">
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary">
          <ArrowRight className="w-4 h-4 mr-2" />
          Levantar Prenda
        </button>
      </div>
    </form>
  );
};

export default Garantias;
